import { useParams, useNavigate } from "@tanstack/react-router";
import { EntityLink } from "@/components/shared/EntityLink";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Printer, FileText, Layers, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getUploadUrl } from "@/lib/utils";
import { formatVnDate } from "@apv-erp/status";
import ImageLightbox from "@/components/shared/ImageLightbox";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import { trpcClient } from "@/lib/trpc";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import PrintDialog from "@/components/shared/PrintDialog";
import React, { useState } from "react";

const isUuidOrCuid = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str) ||
  (str.startsWith("c") && str.length >= 24);

import { TECHNICAL_SPEC_WORKFLOW } from "@/config/workflow-configs";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { StatusBadge } from "@/components/shared/StatusBadge";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { DetailField as F } from "@/components/shared/DetailField";

/** Ô thông tin — cùng kiểu trang chi tiết Sản phẩm (nhãn trái w-24, giá trị đậm; trống hiện —) */

export default function TechnicalSpecDetailPage() {
  const navigate = useNavigate();
  const { specId } = useParams({ strict: false }) as { specId: string };
  const zIndex = useZIndexView();
  const [isPrinting, setIsPrinting] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch: refetchDetail } = useQuery({
    queryKey: ["technicalSpec", specId],
    queryFn: async () =>
      (trpcClient as any).technical.specs.getById.query({ id: specId }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return (trpcClient as any).technical.specs.updateStatus.mutate({
        id: specId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["technicalSpec", specId] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi cập nhật trạng thái");
    },
  });

  const handleUpdateStatus = async (newStatus: string) => {
    return updateStatusMutation.mutateAsync(newStatus);
  };

  const d = data as any;

  // Parse specs if available from product
  let printSizeValue = "";
  let purchaseSizeValue = "";
  let sizeValue = "";
  let outsValue = "";

  if (d?.product?.specs) {
    try {
      const Specs =
        typeof d.product.specs === "string"
          ? JSON.parse(d.product.specs)
          : d.product.specs;
      if (Specs) {
        printSizeValue = Specs.printSize || "";
        purchaseSizeValue = Specs.purchaseSize || "";
        sizeValue = Specs.size || Specs.legacy || "";
        outsValue = Specs.outs || "";
      }
    } catch {}
  }

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Thông số KT", to: "/specs" }]}
        entityLabel="Thông số kỹ thuật"
        backTo="/specs"
        codeOrId={specId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Thông số KT", to: "/specs" }]} />;
  }

  // Parse finishedSize JSON
  let fsObj: any = {};
  let finishedSizeStr = "—";
  if (d.finishedSize) {
    try {
      fsObj =
        typeof d.finishedSize === "string"
          ? JSON.parse(d.finishedSize)
          : d.finishedSize;
      finishedSizeStr =
        fsObj.finishedSizeLength && fsObj.finishedSizeWidth
          ? `${fsObj.finishedSizeLength} x ${fsObj.finishedSizeWidth} x ${fsObj.finishedSizeHeight || 0} mm`
          : fsObj.width && fsObj.height
            ? `${fsObj.width} x ${fsObj.height} mm`
            : "—";

      // Set print size & ups if available
      if (fsObj.printSize) printSizeValue = fsObj.printSize;
      if (fsObj.ups) outsValue = fsObj.ups;
    } catch {}
  }

  const steps = fsObj.steps || {};
  const stepKeys = Object.keys(steps);

  // Evaluate material structure string parsing
  let matStruct = d.materialStructure || "";
  if (matStruct && isUuidOrCuid(matStruct)) {
    matStruct = d.product?.material?.name || "—";
  } else if (!matStruct) {
    matStruct = d.product?.material?.name || d.product?.materialId || "—";
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Thông số KT", to: "/specs" }]}
      title={d.specId || d.productCode}
      entityId={d.id}
      entityType="technical_spec"
      timestamps={{ created: d.createdAt, updated: d.updatedAt }}
      status={<StatusBadge value={d.status || "DRAFT"} type="ApprovalStatus" />}
      workflow={
        <WorkflowStepper
          workflow={TECHNICAL_SPEC_WORKFLOW}
          currentStatus={d.status || "DRAFT"}
          onStatusChange={handleUpdateStatus}
          disabled={updateStatusMutation.isPending}
        />
      }
      actions={
        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPrinting(true)}
          >
            <Printer size={14} className="mr-1.5" /> In phiếu
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate({ to: `/specs/${specId}/edit` as any })}
          >
            <Pencil size={14} className="mr-1.5" /> Sửa
          </Button>
        </div>
      }
      onBack={() => navigate({ to: "/specs" })}
    >
      {/* ═══ 1. THÔNG TIN (phẳng, như trang Sản phẩm): 3 cột thông tin + ảnh sản phẩm ═══ */}
      <div className="border border-slate-200 rounded-lg px-4 py-4 bg-white" data-testid="spec-info-card">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5 items-start">
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
            <F label="Mã TSKT" value={d.specId} mono hl />
            <F
              label="Sản phẩm"
              className="sm:col-span-2"
              value={
                d.productId ? (
                  <EntityLink
                    entityType="product"
                    id={d.productId}
                    code={d.product?.productCode}
                    label={d.productName || d.product?.productName}
                    search={{ returnUrl: `/specs/${specId}` }}
                  />
                ) : (
                  d.productName
                )
              }
            />
            <F label="Phiên bản" value={d.version || "1.0"} />
            <F label="Ngày ban hành" value={formatVnDate(d.issueDate || d.createdAt)} />
            <F label="Chất liệu" value={matStruct} />
            <F label="KT thành phẩm" value={finishedSizeStr !== "—" ? finishedSizeStr : sizeValue} mono />
            <F label="KT trải" value={d.flatSize} mono />
            <F label="Số bát / tờ" value={outsValue} mono hl />
            <F label="Khổ giấy in" value={printSizeValue} mono />
            <F label="Khổ giấy mua" value={purchaseSizeValue} mono />
            <F label="Quy cách đóng gói" value={d.packingSpec || d.product?.packingSpec} />
            <F
              label="Bài bình"
              value={d.layoutId ? <EntityLink entityType="layout" id={d.layoutId} label={d.layout?.name} code={d.layout?.layoutId} /> : null}
            />
            <F
              label="Khuôn bế"
              value={d.dieCutId ? <EntityLink entityType="mold" id={d.dieCutId} label={d.dieCut?.name} code={d.dieCut?.dieCutId} /> : null}
            />
            <F
              label="Khuôn ép nhũ"
              value={d.foilDieId ? <EntityLink entityType="mold" id={d.foilDieId} label={d.foilDie?.name} code={d.foilDie?.dieCutId} /> : null}
            />
            <F label="Lưới in lụa" value={d.screen?.name || d.screenId} mono />
            <F label="Thiết kế" value={<StatusBadge value={d.designStatus || "PENDING"} type="ApprovalStatus" />} />
            <F
              label="File thiết kế"
              value={
                d.designFile ? (
                  <a href={d.designFile} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                    <FileText size={14} /> Mở file
                  </a>
                ) : null
              }
            />
          </div>

          {/* Ảnh sản phẩm */}
          <div className="lg:col-span-1">
            {d.product?.image ? (
              <ImageLightbox src={getUploadUrl(d.product.image)} alt={d.productName || d.specId}>
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white p-1 shadow-sm cursor-zoom-in hover:border-indigo-300">
                  <img src={getUploadUrl(d.product.image)} alt={d.productName || d.specId} className="object-contain w-full h-auto max-h-[220px] rounded-md" />
                </div>
              </ImageLightbox>
            ) : (
              <div className="border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center p-6 flex-col gap-2 min-h-[160px]">
                <ImageIcon className="h-6 w-6 text-slate-300" />
                <span className="text-xs text-slate-600 font-medium">Chưa có hình ảnh</span>
              </div>
            )}
          </div>
        </div>

        {(d.designNotes || d.notes) && (
          <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-2">
            {d.designNotes && <F label="Ghi chú thiết kế" value={<span className="whitespace-pre-wrap font-medium">{d.designNotes}</span>} />}
            {d.notes && <F label="Ghi chú & KCS" value={<span className="whitespace-pre-wrap font-medium">{d.notes}</span>} />}
          </div>
        )}
      </div>

      {/* ═══ 2. CÔNG ĐOẠN & TIÊU CHUẨN KIỂM TRA ═══ */}
      <RelatedDataTabs
        defaultTab="steps"
        tabs={[
          {
            id: "steps",
            label: "Công đoạn & Tiêu chuẩn",
            icon: <Layers className="h-4 w-4" />,
            count: stepKeys.length,
            content:
              stepKeys.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-8">Chưa khai báo tiêu chuẩn kiểm tra theo công đoạn.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="h-9 px-3 w-12 text-center font-semibold">STT</th>
                        <th className="h-9 px-3 w-48 font-semibold">Công đoạn</th>
                        <th className="h-9 px-3 w-[22%] font-semibold">PP kiểm tra</th>
                        <th className="h-9 px-3 w-[30%] font-semibold">Tiêu chuẩn</th>
                        <th className="h-9 px-3 font-semibold">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stepKeys.map((step, idx) => {
                        const stepObj =
                          typeof steps[step] === "object" && steps[step] !== null ? steps[step] : { method: "", standard: "", note: steps[step] };
                        return (
                          <tr key={step} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 align-top">
                            <td className="px-3 py-2 text-center text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-semibold text-slate-800">{step}</td>
                            <td className="px-3 py-2 text-slate-700 whitespace-pre-wrap">{stepObj.method || "—"}</td>
                            <td className="px-3 py-2 text-slate-800 font-medium whitespace-pre-wrap">{stepObj.standard || "—"}</td>
                            <td className="px-3 py-2 text-slate-600 whitespace-pre-wrap">{stepObj.note || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ),
          },
        ]}
      />

      <ZIndexView {...zIndex} />

      <PrintDialog
        open={isPrinting}
        onClose={() => setIsPrinting(false)}
        type="spec"
        data={d}
      />
    </StandardDetailLayout>
  );
}
